import type { SupabaseClient } from '@supabase/supabase-js';
import {
  listBuiltinConnectorTypes,
  type ConnectorRecord,
  type ConnectorTypeMetadata,
} from '@brunel/shared';

export interface ConnectorRegistrySnapshot {
  readonly types: readonly ConnectorTypeMetadata[];
  readonly connectors: readonly ConnectorRecord[];
  readonly vaultAvailable: boolean;
  readonly loaded: boolean;
  readonly error: string | null;
}

export const EMPTY_CONNECTOR_REGISTRY: ConnectorRegistrySnapshot = {
  types: listBuiltinConnectorTypes(),
  connectors: [],
  vaultAvailable: false,
  loaded: false,
  error: null,
};

function mergedTypes(serverTypes: readonly ConnectorTypeMetadata[]): readonly ConnectorTypeMetadata[] {
  const byKey = new Map(listBuiltinConnectorTypes().map((row) => [row.typeKey, row] as const));
  for (const row of serverTypes) byKey.set(row.typeKey, row);
  return [...byKey.values()];
}

export async function loadConnectorRegistry(
  client: Pick<SupabaseClient, 'rpc'>,
  orgId: string,
): Promise<ConnectorRegistrySnapshot> {
  try {
    const [typeResult, connectorResult] = await Promise.all([
      client.rpc('list_connector_types', { request: { orgId } }),
      client.rpc('list_connectors', { request: { orgId } }),
    ]);
    const typeData = typeResult.data as {
      ok?: boolean;
      value?: { types?: ConnectorTypeMetadata[]; vaultAvailable?: boolean };
    } | null;
    const connectorData = connectorResult.data as {
      ok?: boolean;
      value?: { connectors?: ConnectorRecord[]; vaultAvailable?: boolean };
    } | null;
    if (typeResult.error || connectorResult.error || !typeData?.ok || !connectorData?.ok) {
      return {
        ...EMPTY_CONNECTOR_REGISTRY,
        error: 'Connector evidence could not be read. Systems remain Unknown.',
      };
    }
    return {
      types: mergedTypes(typeData.value?.types ?? []),
      connectors: connectorData.value?.connectors ?? [],
      vaultAvailable: typeData.value?.vaultAvailable === true || connectorData.value?.vaultAvailable === true,
      loaded: true,
      error: null,
    };
  } catch {
    return {
      ...EMPTY_CONNECTOR_REGISTRY,
      error: 'Connector evidence could not be read. Systems remain Unknown.',
    };
  }
}
